import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import {
  buildNavigationTargetPath,
  normalizeNavigationTarget,
} from '../lib/appHelpers';
import { createWorkspaceClient } from '../lib/workspaceClient';

function normalizeNotificationDetailItem(item, index) {
  return {
    id: item?.id || `${item?.kind || 'detail'}-${index}`,
    kind: item?.kind || 'queue',
    title: item?.title || 'Detail item',
    message: item?.message || '',
    status: item?.status || 'info',
    navigationTarget: normalizeNavigationTarget(item?.navigationTarget),
    sourceRef:
      item?.sourceRef && typeof item.sourceRef === 'object' && !Array.isArray(item.sourceRef)
        ? item.sourceRef
        : null,
  };
}

function normalizeNotification(notification) {
  return {
    id: notification?._id || notification?.id || '',
    kind: notification?.kind || 'queue.info_only',
    title: notification?.title || 'Notification',
    message: notification?.message || '',
    actorName: notification?.actorName || '',
    actorUserId: notification?.actorUserId || null,
    familyId: notification?.familyId || null,
    navigationTarget: normalizeNavigationTarget(notification?.navigationTarget),
    sourceRef:
      notification?.sourceRef &&
      typeof notification.sourceRef === 'object' &&
      !Array.isArray(notification.sourceRef)
        ? notification.sourceRef
        : null,
    detailItems: Array.isArray(notification?.detailItems)
      ? notification.detailItems.map(normalizeNotificationDetailItem)
      : [],
    readAt: notification?.readAt || null,
    createdAt: notification?.createdAt || null,
    updatedAt: notification?.updatedAt || null,
  };
}

function buildStatusMessage(type, count) {
  if (type === 'loading') {
    return 'Loading notifications...';
  }
  if (count > 0) {
    return 'Notifications loaded.';
  }
  return 'No notifications found for this filter.';
}

export function useNotifications(currentUser, onUnreadCountChange) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [statusFilter, setStatusFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationsStatus, setNotificationsStatus] = useState({
    type: 'idle',
    message: 'Review unread updates and mark notifications read when you are done.',
  });

  const loadNotifications = useCallback(
    async (nextStatus = statusFilter) => {
      if (!currentUser?.userId) {
        setNotifications([]);
        setUnreadCount(0);
        setNotificationsStatus({
          type: 'error',
          message: 'Sign in to review notifications.',
        });
        return false;
      }

      setIsLoadingNotifications(true);
      setNotificationsStatus({
        type: 'loading',
        message: buildStatusMessage('loading', 0),
      });

      try {
        const payload = await workspaceClient.getNotifications(currentUser.userId, {
          status: nextStatus,
          limit: 100,
        });
        const nextNotifications = Array.isArray(payload?.notifications)
          ? payload.notifications.map(normalizeNotification)
          : [];
        const nextUnreadCount =
          typeof payload?.unreadCount === 'number' && Number.isFinite(payload.unreadCount)
            ? payload.unreadCount
            : 0;
        setNotifications(nextNotifications);
        setUnreadCount(nextUnreadCount);
        setNotificationsStatus({
          type: 'success',
          message: buildStatusMessage('success', nextNotifications.length),
        });
        if (typeof onUnreadCountChange === 'function') {
          onUnreadCountChange(nextUnreadCount);
        }
        return true;
      } catch (error) {
        setNotificationsStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not load notifications.',
        });
        return false;
      } finally {
        setIsLoadingNotifications(false);
      }
    },
    [currentUser?.userId, onUnreadCountChange, statusFilter, workspaceClient]
  );

  useEffect(() => {
    void loadNotifications(statusFilter);
  }, [loadNotifications, statusFilter]);

  const updateNotificationReadState = useCallback(
    async (notificationId, action) => {
      if (!currentUser?.userId || !notificationId) {
        return false;
      }

      setNotificationsStatus({
        type: 'loading',
        message: action === 'read' ? 'Marking notification read...' : 'Restoring unread status...',
      });

      try {
        const payload =
          action === 'read'
            ? await workspaceClient.markNotificationRead(currentUser.userId, notificationId)
            : await workspaceClient.markNotificationUnread(currentUser.userId, notificationId);
        const updatedNotification = normalizeNotification(payload?.notification || payload);

        setNotifications((current) => {
          const next = current.map((notification) =>
            notification.id === notificationId ? updatedNotification : notification
          );
          if (statusFilter === 'unread' && updatedNotification.readAt) {
            return next.filter((notification) => notification.id !== notificationId);
          }
          if (statusFilter === 'read' && !updatedNotification.readAt) {
            return next.filter((notification) => notification.id !== notificationId);
          }
          return next;
        });

        const nextUnreadCount = Math.max(
          0,
          unreadCount + (action === 'read' ? -1 : 1)
        );
        setUnreadCount(nextUnreadCount);
        setNotificationsStatus({
          type: 'success',
          message:
            action === 'read'
              ? 'Notification marked read.'
              : 'Notification restored to unread.',
        });
        if (typeof onUnreadCountChange === 'function') {
          onUnreadCountChange(nextUnreadCount);
        }
        return true;
      } catch (error) {
        setNotificationsStatus({
          type: 'error',
          message:
            error?.error ||
            error?.message ||
            (action === 'read'
              ? 'Could not mark notification read.'
              : 'Could not restore unread status.'),
        });
        return false;
      }
    },
    [currentUser?.userId, onUnreadCountChange, statusFilter, unreadCount, workspaceClient]
  );

  return {
    statusFilter,
    notifications,
    unreadCount,
    isLoadingNotifications,
    notificationsStatus,
    setStatusFilter,
    refreshNotifications: loadNotifications,
    markNotificationRead: (notificationId) =>
      updateNotificationReadState(notificationId, 'read'),
    markNotificationUnread: (notificationId) =>
      updateNotificationReadState(notificationId, 'unread'),
    buildNavigationPath: buildNavigationTargetPath,
  };
}

export default useNotifications;
